/* RESPONSIBLE TEAM: team-automation-foundations */
/* eslint-disable @intercom/intercom/no-bare-strings */
import type IntlService from 'ember-intl/services/intl';
// @ts-ignore no type declaration available for ember-copy
import { copy } from 'ember-copy';

export function and(...filters: any) {
  filters = filters.compact();
  if (filters.length === 1) {
    return filters[0];
  }

  return {
    type: 'and',
    filters: filters.flatMap((f: any) => (f.type === 'and' ? f.filters : f)),
  };
}

export function not(filter: any) {
  filter = copy(filter, true);
  if (filter.type === 'category') {
    let values = filter.data.values;
    if (values.length === 1 && typeof values.firstObject === 'boolean') {
      filter.data.values = [!values.firstObject];
    } else {
      filter.type = 'not_in_category';
    }
  } else if (filter.type === 'exists') {
    filter.type = 'not_exists';
  } else {
    throw new Error(`Unsupported filter type: ${filter.type}`);
  }
  return filter;
}

const filters = {
  get chatConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Desktop'],
      },
    };
  },
  get emailConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Email'],
      },
    };
  },

  get sdrParticipated() {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.participated',
        attribute: 'conversation.fin_sdr.participated',
        values: [true],
      },
    };
  },

  routingOutcome(outcomeTitle: string) {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.routing_outcome.title',
        attribute: 'conversation.fin_sdr.routing_outcome_title',
        values: [outcomeTitle],
      },
    };
  },

  get capturedContact() {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.captured_contact',
        attribute: 'conversation.fin_sdr.captured_contact',
        values: [true],
      },
    };
  },

  get noContact() {
    return and(
      {
        type: 'category',
        data: {
          property: 'fin_sdr.captured_contact',
          attribute: 'conversation.fin_sdr.captured_contact',
          values: [false],
        },
      },
      {
        type: 'category',
        data: {
          property: 'fin_sdr.had_prior_contact',
          attribute: 'conversation.fin_sdr.had_prior_contact',
          values: [false],
        },
      },
    );
  },

  get disengaged() {
    return {
      type: 'not_exists',
      data: {
        property: 'fin_sdr.routing_outcome.identifier',
        attribute: 'conversation.fin_sdr.routing_outcome_title',
      },
    };
  },

  get finParticipated() {
    return {
      type: 'category',
      data: {
        property: 'fin.participated',
        attribute: 'conversation.fin.participated',
        values: [true],
      },
    };
  },
  get finNotParticipated() {
    return not(this.finParticipated);
  },
};

// Static translation key mapping to avoid dynamic translation keys
const METRIC_TRANSLATION_KEYS: Record<string, string> = {
  conversations: 'ai-agent.analyze.sales-performance.funnel-metrics.conversations',
  Chat: 'ai-agent.analyze.sales-performance.funnel-metrics.Chat',
  Email: 'ai-agent.analyze.sales-performance.funnel-metrics.Email',
  CapturedContact: 'ai-agent.analyze.sales-performance.funnel-metrics.CapturedContact',
  NoContact: 'ai-agent.analyze.sales-performance.funnel-metrics.NoContact',
  Disengaged: 'ai-agent.analyze.sales-performance.funnel-metrics.Disengaged',
  HandledByServiceAgent: 'ai-agent.analyze.sales-performance.funnel-metrics.HandledByServiceAgent',
  HandledByTeammates: 'ai-agent.analyze.sales-performance.funnel-metrics.HandledByTeammates',
};

export function wrap(filters: any) {
  if (filters && filters.type !== 'and' && filters.type !== 'or') {
    return {
      type: 'and',
      filters: [filters],
    };
  }
  return filters;
}

export function funnelSeries(filters?: any, label?: string) {
  return {
    label,
    metric_id: 'v1.new_conversations',
    filters: wrap(filters),
  };
}

function config(name: string, filters?: any) {
  return { name, series: funnelSeries(filters, name) };
}

export const SdrQualificationFunnelConfig = {
  get filters() {
    return filters;
  },

  buildChartSeriesFilters(routingOutcomes: Array<{ identifier: string; title: string }>) {
    let seriesFilters = [
      config('conversations', this.filters.sdrParticipated),
      config('Chat', and(this.filters.sdrParticipated, this.filters.chatConversations)),
      config('Email', and(this.filters.sdrParticipated, this.filters.emailConversations)),
    ];

    routingOutcomes.forEach((outcome) => {
      seriesFilters.push(
        config(
          outcome.identifier,
          and(this.filters.sdrParticipated, this.filters.routingOutcome(outcome.title)),
        ),
      );
    });

    routingOutcomes.forEach((outcome) => {
      seriesFilters.push(
        config(
          `ChatAnd${outcome.identifier}`,
          and(
            this.filters.sdrParticipated,
            this.filters.chatConversations,
            this.filters.routingOutcome(outcome.title),
          ),
        ),
        config(
          `EmailAnd${outcome.identifier}`,
          and(
            this.filters.sdrParticipated,
            this.filters.emailConversations,
            this.filters.routingOutcome(outcome.title),
          ),
        ),
      );
    });

    let escalateOutcome = routingOutcomes.find(
      (o) => o.identifier.toLowerCase() === 'escalate_to_support',
    );
    if (escalateOutcome) {
      seriesFilters.push(
        config(
          'escalate_to_supportAndHandledByServiceAgent',
          and(
            this.filters.sdrParticipated,
            this.filters.routingOutcome(escalateOutcome.title),
            this.filters.finParticipated,
          ),
        ),
        config(
          'escalate_to_supportAndHandledByTeammates',
          and(
            this.filters.sdrParticipated,
            this.filters.routingOutcome(escalateOutcome.title),
            this.filters.finNotParticipated,
          ),
        ),
      );
    }

    seriesFilters.push(
      config('CapturedContact', and(this.filters.sdrParticipated, this.filters.capturedContact)),
      config('NoContact', and(this.filters.sdrParticipated, this.filters.noContact)),
      config('Disengaged', and(this.filters.sdrParticipated, this.filters.disengaged)),
    );

    seriesFilters.push(
      config(
        'ChatAndDisengaged',
        and(this.filters.sdrParticipated, this.filters.chatConversations, this.filters.disengaged),
      ),
      config(
        'EmailAndDisengaged',
        and(this.filters.sdrParticipated, this.filters.emailConversations, this.filters.disengaged),
      ),
    );

    seriesFilters.push(
      config(
        'DisengagedAndCapturedContact',
        and(this.filters.sdrParticipated, this.filters.disengaged, this.filters.capturedContact),
      ),
      config(
        'DisengagedAndNoContact',
        and(this.filters.sdrParticipated, this.filters.disengaged, this.filters.noContact),
      ),
    );

    return seriesFilters;
  },

  getChartSeriesWithLabels(
    intl: IntlService,
    routingOutcomes: Array<{ identifier: string; title: string }>,
  ) {
    let seriesFilters = this.buildChartSeriesFilters(routingOutcomes);
    let outcomeMap = new Map(routingOutcomes.map((o) => [o.identifier, o.title]));

    return seriesFilters.map((config) => {
      let label: string;

      if (outcomeMap.has(config.name)) {
        label = outcomeMap.get(config.name)!;
      } else if (config.name.includes('And')) {
        let parts = config.name.split('And');
        let labelParts = parts.map((part) => {
          if (outcomeMap.has(part)) {
            return outcomeMap.get(part)!;
          }
          let translationKey = METRIC_TRANSLATION_KEYS[part];
          return translationKey ? intl.t(translationKey) : part;
        });
        label = labelParts.join(' & ');
      } else {
        let translationKey = METRIC_TRANSLATION_KEYS[config.name];
        label = translationKey ? intl.t(translationKey) : config.name;
      }

      return {
        ...config.series,
        label,
      };
    });
  },

  getMetricsFromChartDataRequest(
    chartData: any,
    routingOutcomes: Array<{ identifier: string; title: string }>,
  ) {
    let seriesFilters = this.buildChartSeriesFilters(routingOutcomes);
    let result: any = {};
    for (let i = 0; i < chartData.length; i++) {
      let seriesName = seriesFilters[i].name;
      let seriesData = chartData[i].data[0];
      result[seriesName] = seriesData;
    }
    return result;
  },
};
